/**
 * Field Help — Email Campaigns
 *
 * Keys MUST match the field ids in
 * `features/email-campaigns/components/EmailCampaignsConfig.ts` so the "?"
 * popover attaches to the right column / editor row / card field.
 *
 * An email_campaign belongs to exactly ONE brand (WTC or Schooley Mitchell, see
 * MARKETING_BRANDS). It references an email_template OR carries its own
 * subject/body, targets an email_audience, and is delivered by the send cron
 * (/api/cron/email-campaign-send) via AWS SES with per-recipient open/click
 * tracking + an unsubscribe footer.
 */

import type { EntityFieldHelp } from "./types";

export const emailCampaignFieldHelp: EntityFieldHelp = {
  name: {
    title: "Name",
    description: "An internal label for the campaign — not shown to recipients.",
    usage: "Use it to find the campaign in lists and on the marketing calendar. Recipients see the Subject, never this name.",
  },
  subject: {
    title: "Subject",
    description: "The email subject line recipients see in their inbox.",
    usage: "If the campaign references a template, this can override the template's subject. Keep it concise and on-brand.",
  },
  status: {
    title: "Status",
    description: "Where the campaign is in its lifecycle: Draft → Scheduled → Sending → Sent (or Partially Failed / Failed / Canceled).",
    usage: "Set by the send pipeline, not edited freely. 'Partially Failed' means some recipients received it and some didn't — check the per-recipient records.",
    relatedTo: "The send cron moves Scheduled campaigns to Sending, then to a terminal status once every recipient has been attempted.",
  },
  brand: {
    title: "Brand",
    description: "Which marketing brand the campaign belongs to — West Tennessee Consulting or Schooley Mitchell.",
    usage: "Brand decides the default from/reply-to identity, voice, and which unsubscribe suppression list applies. A campaign is always exactly one brand.",
    relatedTo: "Unsubscribes are scoped per brand: unsubscribing from a WTC campaign does not suppress Schooley Mitchell email, and vice versa.",
  },
  from_name: {
    title: "From Name",
    description: "The display name recipients see as the sender.",
    usage: "Seeded from the brand's email defaults; the campaign keeps its own copy so historical sends stay stable.",
  },
  from_email: {
    title: "From Email",
    description: "The verified sending address the email is sent from.",
    usage: "Must be an SES-verified sender for the brand. Seeded from the brand's email defaults.",
  },
  reply_to: {
    title: "Reply-To",
    description: "Where replies to the campaign are routed.",
    usage: "Often the same as From Email. Set it to a monitored inbox so replies aren't lost.",
  },
  scheduled_at: {
    title: "Scheduled",
    description: "When the campaign is queued to send (blank = not scheduled).",
    usage: "Displayed in WTC business time (America/Chicago). The send cron picks up campaigns whose scheduled time has passed.",
    relatedTo: "Storage is UTC; the value is shown in Central so it matches how the business plans sends.",
  },
  sent_at: {
    title: "Sent",
    description: "When the campaign finished sending to its audience.",
    usage: "Set by the send pipeline once every recipient has been attempted.",
  },
  recipients_count: {
    title: "Recipients",
    description: "How many recipients the campaign's audience resolved to.",
    usage: "Projected from the campaign's stats. Recipients already unsubscribed for the brand are skipped and not counted as sent.",
  },
  sent_count: {
    title: "Sent",
    description: "How many recipients were successfully handed to the mail provider.",
    usage: "Projected from the campaign's stats. Compare against Recipients to spot delivery failures.",
  },
  opened_count: {
    title: "Opened",
    description: "How many recipients opened the email (tracked via the open pixel).",
    usage: "Projected from the campaign's stats. Open tracking is best-effort — some mail clients block the pixel.",
  },
  clicked_count: {
    title: "Clicked",
    description: "How many recipients clicked a tracked link in the email.",
    usage: "Projected from the campaign's stats. Links are rewritten through the click endpoint so clicks can be attributed.",
  },
};
